import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { MessageCircle, X, Send } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { useSettings, waLink, EASE } from "../lib/site";
import { WhatsAppIcon } from "./Icons";
import { LogoMark } from "./Logo";

const KEY = "aura_chat";
const loadConv = () => { try { return JSON.parse(localStorage.getItem(KEY)) || null; } catch { return null; } };

const StartForm = ({ onStart }) => {
  const [f, setF] = useState({ name: "", contact: "", text: "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try { onStart((await api.post("/chat/conversations", f)).data); } catch (x) { setErr(errMsg(x)); } finally { setBusy(false); }
  };
  const field = "w-full rounded-xl border border-[var(--ink)]/15 bg-white/70 px-4 py-3 text-sm outline-none transition-colors focus:border-[var(--gold)]";
  return (
    <form onSubmit={submit} className="space-y-3 p-5" data-testid="chat-start-form">
      <p className="text-sm leading-relaxed text-[var(--ink-soft)]">Ciao! Lasciaci il tuo nome e scrivi la tua richiesta: ti rispondiamo qui appena possibile.</p>
      <input data-testid="chat-name-input" required maxLength={60} placeholder="Il tuo nome" className={field} value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} />
      <input data-testid="chat-contact-input" maxLength={80} placeholder="Telefono o email (facoltativo)" className={field} value={f.contact} onChange={(e) => setF({ ...f, contact: e.target.value })} />
      <textarea data-testid="chat-first-message-input" required maxLength={1000} rows={3} placeholder="Es. Vorrei prenotare un microblading..." className={field} value={f.text} onChange={(e) => setF({ ...f, text: e.target.value })} />
      {err && <p data-testid="chat-error" className="text-xs text-red-700">{err}</p>}
      <button data-testid="chat-start-submit" disabled={busy} className="w-full rounded-full bg-[var(--ink)] py-3 text-sm text-[var(--cream)] transition-colors hover:bg-[var(--gold-deep)] disabled:opacity-60">{busy ? "Invio..." : "Inizia la chat"}</button>
    </form>
  );
};

const Thread = ({ conv, onReset }) => {
  const [msgs, setMsgs] = useState(conv.messages || []);
  const [text, setText] = useState("");
  const endRef = useRef(null);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try { const { data } = await api.get(`/chat/conversations/${conv.id}/messages`); if (alive && Array.isArray(data)) setMsgs(data); }
      catch (e) { if (e?.response?.status === 404) onReset(); }
    };
    load();
    const t = setInterval(load, 4000);
    return () => { alive = false; clearInterval(t); };
  }, [conv.id, onReset]);

  useEffect(() => { endRef.current?.scrollIntoView({ block: "end" }); }, [msgs.length]);

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    const t = text; setText("");
    try { const { data } = await api.post(`/chat/conversations/${conv.id}/messages`, { text: t }); setMsgs((m) => [...m, data]); } catch { setText(t); }
  };

  return (
    <>
      <div data-lenis-prevent data-testid="chat-messages" className="flex-1 space-y-3 overflow-y-auto p-5">
        {msgs.map((m) => (
          <div key={m.id} className={`flex ${m.sender === "client" ? "justify-end" : "justify-start"}`}>
            <p data-testid={`chat-message-${m.sender}`} className={`max-w-[80%] whitespace-pre-wrap rounded-2xl px-4 py-2.5 text-sm ${m.sender === "client" ? "rounded-br-sm bg-[var(--ink)] text-[var(--cream)]" : "rounded-bl-sm bg-white text-[var(--ink)]"}`}>{m.text}</p>
          </div>
        ))}
        {msgs.length > 0 && msgs.every((m) => m.sender === "client") && <p className="text-center text-xs text-[var(--ink-soft)]">Messaggio ricevuto. Ti risponderemo qui al più presto.</p>}
        <div ref={endRef} />
      </div>
      <form onSubmit={send} className="flex gap-2 border-t border-[var(--ink)]/10 p-3">
        <input data-testid="chat-message-input" value={text} onChange={(e) => setText(e.target.value)} maxLength={1000} placeholder="Scrivi un messaggio..." className="flex-1 rounded-full border border-[var(--ink)]/15 bg-white/80 px-4 py-2.5 text-sm outline-none focus:border-[var(--gold)]" />
        <button data-testid="chat-send-button" aria-label="Invia" className="grid h-11 w-11 place-items-center rounded-full bg-[var(--gold)] text-[var(--ink)] transition-transform hover:scale-105"><Send size={16} /></button>
      </form>
    </>
  );
};

export const ChatWidget = () => {
  const s = useSettings();
  const [open, setOpen] = useState(false);
  const [conv, setConv] = useState(loadConv);

  useEffect(() => {
    const h = () => setOpen(true);
    window.addEventListener("aura:open-chat", h);
    return () => window.removeEventListener("aura:open-chat", h);
  }, []);

  const start = (c) => { localStorage.setItem(KEY, JSON.stringify({ id: c.id, name: c.name })); setConv(c); };
  const reset = useRef(() => { localStorage.removeItem(KEY); setConv(null); }).current;

  return (
    <div data-testid="floating-chat-widget" className="fixed bottom-5 right-5 z-[55] flex flex-col items-end gap-3">
      <AnimatePresence>
        {open && (
          <motion.div data-testid="chat-panel" initial={{ opacity: 0, y: 24, scale: 0.96 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 24, scale: 0.96 }} transition={{ duration: 0.45, ease: EASE }} className="flex h-[min(560px,calc(100svh-120px))] w-[calc(100vw-40px)] max-w-[380px] origin-bottom-right flex-col overflow-hidden rounded-[1.75rem] border border-[var(--gold)]/25 bg-[var(--cream)]/95 shadow-[0_30px_80px_rgba(42,30,27,.22)] backdrop-blur-xl">
            <div className="flex items-center justify-between bg-[var(--ink)] px-5 py-4 text-[var(--cream)]">
              <div className="flex items-center gap-3">
                <LogoMark className="h-9 w-9 text-[var(--gold)]" />
                <div><p className="font-display text-lg leading-none tracking-[0.2em]">AURA</p><p className="mt-1 text-[11px] text-[var(--nude)]">Chat con lo studio</p></div>
              </div>
              <button data-testid="chat-close-button" aria-label="Chiudi" onClick={() => setOpen(false)} className="grid h-9 w-9 place-items-center rounded-full hover:bg-white/10"><X size={18} /></button>
            </div>
            {conv ? <Thread conv={conv} onReset={reset} /> : <StartForm onStart={start} />}
            <a data-testid="chat-whatsapp-link" href={waLink(s)} target="_blank" rel="noreferrer" className="flex items-center justify-center gap-2 border-t border-[var(--ink)]/10 py-3 text-xs text-[var(--ink-soft)] hover:text-[var(--gold-deep)]"><WhatsAppIcon className="h-3.5 w-3.5" /> Preferisci WhatsApp?</a>
          </motion.div>
        )}
      </AnimatePresence>
      <div className="flex items-center gap-3">
        <motion.a whileHover={{ y: -3 }} data-testid="floating-whatsapp-button" href={waLink(s)} target="_blank" rel="noreferrer" aria-label="WhatsApp" className="grid h-12 w-12 place-items-center rounded-full border border-[var(--gold)]/30 bg-[var(--cream)] text-[var(--ink)] shadow-lg"><WhatsAppIcon className="h-5 w-5" /></motion.a>
        <motion.button whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }} data-testid="chat-launcher-button" onClick={() => setOpen((v) => !v)} aria-label="Chat" className="grid h-16 w-16 place-items-center rounded-full bg-[var(--ink)] text-[var(--cream)] shadow-[0_18px_40px_rgba(42,30,27,.35)]">
          {open ? <X size={22} /> : <MessageCircle size={22} />}
        </motion.button>
      </div>
    </div>
  );
};
